import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CloudRain, ArrowLeft, MapPin, Calendar, Clock, Thermometer, Drop, Wind, FileArrowDown, Warning, ShieldCheck, HardHat, House, Plant, User, Copy, Info, Timer, CalendarBlank, Lightning, ShareNetwork } from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';
import ShareReportModal from '@/components/ShareReportModal';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;

const QUERY_MODES = [
  { key: 'single', label: 'Single Query', icon: Clock, desc: 'One date & time' },
  { key: 'range', label: 'Date Range', icon: CalendarBlank, desc: 'Hourly timeline' },
  { key: 'last24h', label: 'Last 24 Hours', icon: Lightning, desc: 'Quick check' },
];

const USE_CASES = {
  insurance: {
    label: 'Insurance Claim',
    icon: ShieldCheck,
    subtitle: 'Retrieve verified weather records for your insurance claim documentation',
    reportLine: 'Verified weather conditions for insurance claim support',
    checkmarks: [
      'Timestamped conditions for your claim period',
      'Location verified with coordinates and timezone',
      'Station-level observations for claim accuracy',
      'Formatted for insurance adjuster review',
    ],
  },
  construction: {
    label: 'Construction Delay',
    icon: HardHat,
    subtitle: 'Document weather conditions that impacted your project timeline',
    reportLine: 'Weather documentation for construction delay verification',
    checkmarks: [
      'Timestamped conditions during the delay period',
      'Location verified at your job site coordinates',
      'Wind, precipitation, and temperature records',
      'Formatted for contractor and project manager review',
    ],
  },
  property: {
    label: 'Property Damage',
    icon: House,
    subtitle: 'Establish weather conditions at the time of property damage',
    reportLine: 'Weather conditions report for property damage documentation',
    checkmarks: [
      'Timestamped conditions at the time of damage',
      'Location verified at your property coordinates',
      'Storm intensity, wind speed, and precipitation data',
      'Formatted for claims adjusters and legal review',
    ],
  },
  agriculture: {
    label: 'Agriculture',
    icon: Plant,
    subtitle: 'Access historical weather data relevant to crop and livestock records',
    reportLine: 'Historical weather report for agricultural documentation',
    checkmarks: [
      'Temperature and humidity for your growing period',
      'Location verified at your field coordinates',
      'Precipitation and cloud cover observations',
      'Formatted for agricultural agency review',
    ],
  },
  personal: {
    label: 'Personal Reference',
    icon: User,
    subtitle: 'Look up past weather for personal records or curiosity',
    reportLine: 'Historical weather conditions for personal reference',
    checkmarks: [
      'Timestamped historical weather data',
      'Location verified (coordinates + timezone)',
      'Includes station observations',
      'Ready for documentation and claims',
    ],
  },
};

const WeatherLookup = () => {
  const { user } = useAuth();
  const [queryMode, setQueryMode] = useState('single');
  const [location, setLocation] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('08:00');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('20:00');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [useCase, setUseCase] = useState(null);
  const [downloadedReportId, setDownloadedReportId] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const queryId = params.get('query_id');
    
    if (sessionId && queryId) {
      handlePaymentReturn(sessionId, queryId);
      window.history.replaceState({}, document.title, '/lookup');
    }
  }, []);

  const handlePaymentReturn = async (sessionId, queryId) => {
    setCheckingPayment(true);
    try {
      const { data: statusData } = await axios.get(
        `${BACKEND_URL}/api/payments/status/${sessionId}`,
        { withCredentials: true }
      );
      
      if (statusData.payment_status === 'paid') {
        const { data: reportData } = await axios.post(
          `${BACKEND_URL}/api/weather/report/generate?query_id=${queryId}&session_id=${sessionId}`,
          {},
          { withCredentials: true }
        );
        
        if (reportData.already_generated) {
          toast.success('This report was already generated and sent to your email.');
        } else {
          toast.success(
            `✅ Report Generated Successfully\n\nYour official weather report is ready.\n• Report ID: ${reportData.report_id}\n• A copy has been sent to your email\n\nThank you for using WeatherProof.`,
            { duration: 8000 }
          );
        }
      }
    } catch (error) {
      toast.error('Failed to generate report after payment');
    } finally {
      setCheckingPayment(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    
    try {
      let data;
      if (queryMode === 'single') {
        const res = await axios.post(
          `${BACKEND_URL}/api/weather/lookup`,
          { location, date, time, use_case: useCase || 'personal' },
          { withCredentials: true }
        );
        data = res.data;
      } else if (queryMode === 'range') {
        const res = await axios.post(
          `${BACKEND_URL}/api/weather/range-lookup`,
          { location, start_date: startDate, start_time: startTime, end_date: endDate, end_time: endTime, use_case: useCase || 'personal' },
          { withCredentials: true }
        );
        data = res.data;
      } else {
        const res = await axios.post(
          `${BACKEND_URL}/api/weather/last-24h`,
          { location, use_case: useCase || 'personal' },
          { withCredentials: true }
        );
        data = res.data;
      }
      setResult(data);
      toast.success('Weather data retrieved successfully');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'ERROR: Unable to retrieve weather data. Please try again.';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePDF = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/create-payment-session?query_id=${result.query_id}`,
        {},
        { withCredentials: true }
      );
      window.location.href = data.checkout_url;
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Payment session creation failed. Please try again.';
      toast.error(errorMsg);
      setGenerating(false);
    }
  };

  const handleDownloadPreview = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/weather/report/preview?query_id=${result.query_id}`,
        {},
        { withCredentials: true }
      );
      if (data.pdf_data) {
        const byteArray = new Uint8Array(data.pdf_data.match(/.{1,2}/g).map(b => parseInt(b, 16)));
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `WeatherProof_${data.report_id}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        setDownloadedReportId(data.report_id);
        toast.success(`Report downloaded (ID: ${data.report_id})`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-white mobile-nav-padding">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2">
              <CloudRain size={28} weight="bold" className="text-blue-600" />
              <span className="text-xl sm:text-2xl font-bold tracking-tight text-slate-950" style={{ fontFamily: 'Chivo' }}>WeatherProof</span>
            </Link>
            <Link
              to="/dashboard"
              data-testid="back-to-dashboard-link"
              className="text-sm text-slate-600 hover:text-slate-950 transition-colors flex items-center gap-1.5"
            >
              <ArrowLeft size={16} weight="bold" />
              <span className="hidden sm:inline">Back to Dashboard</span>
              <span className="sm:hidden">Back</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
        <div className="mb-12">
          <h1 className="text-4xl font-bold tracking-tighter text-slate-950" style={{ fontFamily: 'Chivo' }}>Historical Weather Lookup</h1>
          <p className="text-sm text-slate-600 mt-2">
            {useCase && USE_CASES[useCase] ? USE_CASES[useCase].subtitle : 'Retrieve accurate weather conditions for any past date and location'}
          </p>
        </div>

        {/* Query Mode Selector */}
        {!result && (
          <div className="mb-6" data-testid="query-mode-selector">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-3">Query Mode</p>
            <div className="grid grid-cols-3 gap-3">
              {QUERY_MODES.map(({ key, label, icon: Icon, desc }) => {
                const active = queryMode === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => { setQueryMode(key); setResult(null); }}
                    data-testid={`mode-${key}`}
                    className={`flex flex-col items-center gap-1.5 p-3 sm:p-4 border text-center transition-all ${
                      active
                        ? 'border-slate-950 bg-slate-950 text-white'
                        : 'border-slate-200 bg-white hover:border-slate-300 text-slate-600'
                    }`}
                  >
                    <Icon size={20} weight={active ? 'bold' : 'regular'} className={active ? 'text-white' : 'text-slate-400'} />
                    <span className="text-xs font-medium">{label}</span>
                    <span className={`text-[10px] ${active ? 'text-slate-300' : 'text-slate-400'} hidden sm:block`}>{desc}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Use Case Selector */}
        {!result && (
          <div className="mb-8" data-testid="use-case-selector">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-3">What are you using this report for?</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {Object.entries(USE_CASES).map(([key, uc]) => {
                const Icon = uc.icon;
                const selected = useCase === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setUseCase(key)}
                    data-testid={`use-case-${key}`}
                    className={`flex flex-col items-center gap-2 p-4 border text-center transition-all ${
                      selected
                        ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-600 ring-offset-1'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <Icon size={22} weight={selected ? 'bold' : 'regular'} className={selected ? 'text-blue-600' : 'text-slate-400'} />
                    <span className={`text-xs font-medium ${selected ? 'text-blue-700' : 'text-slate-600'}`}>{uc.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Lookup Form */}
        <div className="bg-white border border-slate-200 p-6 sm:p-8 mb-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Location — always shown */}
            <div>
              <label className="block text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-2">
                <MapPin size={14} className="inline mr-1" />
                Location
              </label>
              <input
                data-testid="lookup-location-input"
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                required
                className="w-full px-4 py-2 border border-slate-300 focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 focus:outline-none text-sm"
                placeholder="City, ZIP, or address"
              />
            </div>

            {/* Single Mode — date + time */}
            {queryMode === 'single' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-2">
                    <Calendar size={14} className="inline mr-1" />
                    Date
                  </label>
                  <input
                    data-testid="lookup-date-input"
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    required
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-2 border border-slate-300 focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 focus:outline-none text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-2">
                    <Clock size={14} className="inline mr-1" />
                    Time
                  </label>
                  <input
                    data-testid="lookup-time-input"
                    type="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-slate-300 focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 focus:outline-none text-sm font-mono"
                  />
                </div>
              </div>
            )}

            {/* Range Mode — start/end date+time */}
            {queryMode === 'range' && (
              <div className="space-y-4">
                <p className="text-xs text-slate-500">Returns hourly weather data for the selected period (max 30 days).</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-2">Start Date</label>
                    <input
                      data-testid="range-start-date"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      required
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-2 border border-slate-300 focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 focus:outline-none text-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-2">Start Time</label>
                    <input
                      data-testid="range-start-time"
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      required
                      className="w-full px-4 py-2 border border-slate-300 focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 focus:outline-none text-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-2">End Date</label>
                    <input
                      data-testid="range-end-date"
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      required
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-2 border border-slate-300 focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 focus:outline-none text-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-2">End Time</label>
                    <input
                      data-testid="range-end-time"
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      required
                      className="w-full px-4 py-2 border border-slate-300 focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 focus:outline-none text-sm font-mono"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Last 24h Mode — no date inputs needed */}
            {queryMode === 'last24h' && (
              <div className="p-4 bg-slate-50 border border-slate-200">
                <p className="text-sm text-slate-700">
                  Automatically retrieves hourly weather data for the <strong>last 24 hours</strong> at the specified location. No date or time input required.
                </p>
              </div>
            )}

            <button
              data-testid="lookup-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full px-6 py-3 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors text-sm font-medium"
            >
              {loading
                ? 'Retrieving historical weather data...'
                : queryMode === 'last24h'
                  ? 'Retrieve Last 24 Hours'
                  : queryMode === 'range'
                    ? 'Retrieve Weather Timeline'
                    : 'Retrieve Weather Data'
              }
            </button>
          </form>

          {loading && (
            <div className="mt-6 p-5 bg-blue-50 border border-blue-200" data-testid="lookup-loading-state">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
                <p className="text-sm font-medium text-blue-800">Retrieving historical weather data...</p>
              </div>
              <div className="space-y-2.5 pl-8">
                <div className="flex items-center gap-2 text-xs text-blue-700 animate-pulse">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                  Locating nearest reporting stations
                </div>
                <div className="flex items-center gap-2 text-xs text-blue-700 animate-pulse" style={{ animationDelay: '0.3s' }}>
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                  Verifying timestamp accuracy
                </div>
                <div className="flex items-center gap-2 text-xs text-blue-700 animate-pulse" style={{ animationDelay: '0.6s' }}>
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                  Compiling weather observations
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Results Display */}
        {result && (
          <div data-testid="weather-results" className="space-y-6">
            {/* Query ID Bar */}
            <div className="flex items-center justify-between p-4 bg-slate-950 text-white">
              <div className="flex items-center gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Query ID (Reference Number)</p>
                  <p className="text-sm font-mono mt-0.5" data-testid="query-id-display">{result.query_id}</p>
                </div>
                <div className="group relative">
                  <Info size={14} weight="bold" className="text-slate-500 hover:text-slate-300 cursor-help" />
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-white text-slate-700 text-xs whitespace-nowrap border border-slate-200 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    Use this ID for support or documentation reference
                  </div>
                </div>
              </div>
              <button
                data-testid="copy-query-id-btn"
                onClick={() => { navigator.clipboard.writeText(result.query_id); toast.success('Query ID copied to clipboard'); }}
                className="px-3 py-1 text-xs font-medium bg-slate-800 hover:bg-slate-700 transition-colors flex items-center gap-1.5"
              >
                <Copy size={14} weight="bold" />
                Copy
              </button>
            </div>

            {/* Location Info */}
            <div className="bg-slate-50 border border-slate-200 p-6">
              <h2 className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-4">Location Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Address</p>
                  <p className="text-sm font-mono text-slate-950">{result.location.formatted_address || result.location.name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Latitude</p>
                  <p className="text-sm font-mono text-slate-950">{result.location.latitude.toFixed(6)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Longitude</p>
                  <p className="text-sm font-mono text-slate-950">{result.location.longitude.toFixed(6)}</p>
                </div>
              </div>
            </div>

            {/* Weather Data — Single Mode */}
            {(!result.mode || result.mode === 'single') && (
              <div className="bg-white border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Weather Observation</h2>
                  {result.weather_data?.is_exact_match === false && (
                    <div className="flex items-center gap-2 px-3 py-1 bg-amber-50 text-amber-600 border border-amber-200">
                      <Warning size={16} weight="bold" />
                      <span className="text-xs font-medium">Nearest Available Reading</span>
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6">
                  <div className="border border-slate-200 p-4 sm:p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Thermometer size={20} weight="bold" className="text-blue-600" />
                      <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Temperature</p>
                    </div>
                    <p className="text-2xl sm:text-3xl font-mono font-bold text-slate-950">
                      {result.weather_data?.weather_data?.temp || 'N/A'}<span className="text-lg sm:text-xl text-slate-500">°F</span>
                    </p>
                  </div>
                  
                  <div className="border border-slate-200 p-4 sm:p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Drop size={20} weight="bold" className="text-blue-600" />
                      <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Humidity</p>
                    </div>
                    <p className="text-2xl sm:text-3xl font-mono font-bold text-slate-950">
                      {result.weather_data?.weather_data?.humidity || 'N/A'}<span className="text-lg sm:text-xl text-slate-500">%</span>
                    </p>
                  </div>
                  
                  <div className="border border-slate-200 p-4 sm:p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Wind size={20} weight="bold" className="text-blue-600" />
                      <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Wind Speed</p>
                    </div>
                    <p className="text-2xl sm:text-3xl font-mono font-bold text-slate-950">
                      {result.weather_data?.weather_data?.windspeed || 'N/A'}<span className="text-lg sm:text-xl text-slate-500">mph</span>
                    </p>
                  </div>
                  
                  <div className="border border-slate-200 p-4 sm:p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Wind size={20} weight="bold" className="text-blue-600" />
                      <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Conditions</p>
                    </div>
                    <p className="text-sm font-medium text-slate-950">
                      {result.weather_data?.weather_data?.conditions || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Weather Timeline — Range / Last 24h */}
            {(result.mode === 'range' || result.mode === 'last24h') && result.weather_data?.timeline && (
              <div className="bg-white border border-slate-200" data-testid="weather-timeline">
                <div className="p-6 border-b border-slate-200">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <h2 className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">
                      Hourly Weather Timeline
                    </h2>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-mono text-slate-500">{result.weather_data.total_hours} hours</span>
                      <span className="text-xs text-slate-400">|</span>
                      <span className="text-xs font-mono text-slate-500">{result.weather_data.requested_range}</span>
                    </div>
                  </div>
                </div>

                {/* Summary Stats */}
                {(() => {
                  const tl = result.weather_data.timeline;
                  const temps = tl.map(r => r.temp).filter(Boolean);
                  const winds = tl.map(r => r.windspeed).filter(Boolean);
                  const minT = Math.min(...temps);
                  const maxT = Math.max(...temps);
                  const avgT = (temps.reduce((a, b) => a + b, 0) / temps.length);
                  const maxW = Math.max(...winds);
                  return (
                    <div className="grid grid-cols-2 sm:grid-cols-4 border-b border-slate-200">
                      <div className="p-4 sm:p-5 border-r border-slate-200">
                        <p className="text-[10px] uppercase tracking-[0.15em] text-slate-400 mb-1">Low</p>
                        <p className="text-xl sm:text-2xl font-mono font-bold text-blue-600">{minT.toFixed(1)}°</p>
                      </div>
                      <div className="p-4 sm:p-5 sm:border-r border-slate-200">
                        <p className="text-[10px] uppercase tracking-[0.15em] text-slate-400 mb-1">High</p>
                        <p className="text-xl sm:text-2xl font-mono font-bold text-red-500">{maxT.toFixed(1)}°</p>
                      </div>
                      <div className="p-4 sm:p-5 border-r border-t sm:border-t-0 border-slate-200">
                        <p className="text-[10px] uppercase tracking-[0.15em] text-slate-400 mb-1">Avg Temp</p>
                        <p className="text-xl sm:text-2xl font-mono font-bold text-slate-950">{avgT.toFixed(1)}°</p>
                      </div>
                      <div className="p-4 sm:p-5 border-t sm:border-t-0 border-slate-200">
                        <p className="text-[10px] uppercase tracking-[0.15em] text-slate-400 mb-1">Max Wind</p>
                        <p className="text-xl sm:text-2xl font-mono font-bold text-slate-950">{maxW.toFixed(1)}<span className="text-sm text-slate-400"> mph</span></p>
                      </div>
                    </div>
                  );
                })()}

                {/* Timeline Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-left" data-testid="timeline-table">
                    <thead>
                      <tr className="bg-slate-50 text-[10px] uppercase tracking-[0.15em] text-slate-400">
                        <th className="px-4 py-3 font-medium whitespace-nowrap">Time</th>
                        <th className="px-4 py-3 font-medium text-right">Temp</th>
                        <th className="px-4 py-3 font-medium text-right">Humidity</th>
                        <th className="px-4 py-3 font-medium text-right">Wind</th>
                        <th className="px-4 py-3 font-medium">Conditions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.weather_data.timeline.map((row, i) => (
                        <tr key={i} className={`border-t border-slate-100 ${i % 2 === 0 ? '' : 'bg-slate-50/50'}`}>
                          <td className="px-4 py-2.5 text-xs font-mono text-slate-700 whitespace-nowrap">
                            {row.date?.slice(5)} {row.time?.slice(0, 5)}
                          </td>
                          <td className="px-4 py-2.5 text-xs font-mono text-slate-950 text-right font-semibold">
                            {row.temp != null ? `${row.temp}°F` : '–'}
                          </td>
                          <td className="px-4 py-2.5 text-xs font-mono text-slate-600 text-right">
                            {row.humidity != null ? `${row.humidity}%` : '–'}
                          </td>
                          <td className="px-4 py-2.5 text-xs font-mono text-slate-600 text-right">
                            {row.windspeed != null ? `${row.windspeed} mph` : '–'}
                          </td>
                          <td className="px-4 py-2.5 text-xs text-slate-600">
                            {row.conditions || '–'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Source Transparency */}
            <div className="bg-blue-50 border border-blue-200 p-6">
              <h2 className="text-xs font-medium uppercase tracking-[0.2em] text-blue-900 mb-4">Source Transparency</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-blue-700 mb-1">Data Provider</p>
                  <p className="text-sm font-mono text-blue-950">{result.weather_data?.provider || 'Unknown'}</p>
                </div>
                {result.mode === 'range' || result.mode === 'last24h' ? (
                  <>
                    <div>
                      <p className="text-xs text-blue-700 mb-1">Query Mode</p>
                      <p className="text-sm font-mono text-blue-950">{result.mode === 'last24h' ? 'Last 24 Hours' : 'Date Range'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-700 mb-1">Period</p>
                      <p className="text-sm font-mono text-blue-950">{result.weather_data?.requested_range}</p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-700 mb-1">Data Points</p>
                      <p className="text-sm font-mono text-blue-950">{result.weather_data?.total_hours} hourly observations</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <p className="text-xs text-blue-700 mb-1">Data Source</p>
                      <p className="text-sm font-mono text-blue-950">{result.weather_data?.source?.toUpperCase() || 'OBS'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-700 mb-1">Requested Time</p>
                      <p className="text-sm font-mono text-blue-950">{result.weather_data?.requested_datetime}</p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-700 mb-1">Observation Time</p>
                      <p className="text-sm font-mono text-blue-950">{result.weather_data?.observation_datetime || 'N/A'}</p>
                    </div>
                  </>
                )}
                {result.weather_data?.stations && result.weather_data.stations.length > 0 && (
                  <div className="md:col-span-2">
                    <p className="text-xs text-blue-700 mb-1">Weather Stations</p>
                    <p className="text-sm font-mono text-blue-950">{result.weather_data.stations.join(', ')}</p>
                  </div>
                )}
              </div>
              {result.weather_data?.warning && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-300">
                  <p className="text-xs text-amber-800"><Warning size={14} className="inline mr-1" /> {result.weather_data.warning}</p>
                </div>
              )}
            </div>

            {/* Use Case Selector (in results) */}
            <div className="bg-white border border-slate-200 p-6" data-testid="use-case-selector-results">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-3">What are you using this report for?</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {Object.entries(USE_CASES).map(([key, uc]) => {
                  const Icon = uc.icon;
                  const selected = useCase === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setUseCase(key)}
                      data-testid={`use-case-result-${key}`}
                      className={`flex flex-col items-center gap-2 p-3 border text-center transition-all ${
                        selected
                          ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-600 ring-offset-1'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                    >
                      <Icon size={20} weight={selected ? 'bold' : 'regular'} className={selected ? 'text-blue-600' : 'text-slate-400'} />
                      <span className={`text-xs font-medium ${selected ? 'text-blue-700' : 'text-slate-600'}`}>{uc.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Value Reinforcement */}
            <div className="bg-emerald-50 border border-emerald-200 p-6" data-testid="value-reinforcement">
              <h3 className="text-xs font-medium uppercase tracking-[0.2em] text-emerald-800 mb-4">This report can help you</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex items-center gap-2 text-sm text-emerald-800">
                  <svg className="w-4 h-4 text-emerald-600 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                  Document weather conditions for disputes
                </div>
                <div className="flex items-center gap-2 text-sm text-emerald-800">
                  <svg className="w-4 h-4 text-emerald-600 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                  Support insurance or property claims
                </div>
                <div className="flex items-center gap-2 text-sm text-emerald-800">
                  <svg className="w-4 h-4 text-emerald-600 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                  Verify jobsite conditions
                </div>
                <div className="flex items-center gap-2 text-sm text-emerald-800">
                  <svg className="w-4 h-4 text-emerald-600 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                  Keep records for future reference
                </div>
              </div>
            </div>

            {/* Preview Mode */}
            <div className="bg-slate-50 border border-slate-200 p-8" data-testid="preview-mode-section">
              <h3 className="text-base font-semibold text-slate-950 mb-1" style={{ fontFamily: 'Chivo' }}>Preview Mode</h3>
              <p className="text-sm text-slate-600">
                You are viewing a basic summary.<br />
                Download the official report to get a formatted, timestamped document for documentation and claims.
              </p>
            </div>

            {/* Report Includes */}
            <div className="bg-white border border-slate-200 p-6" data-testid="report-includes">
              <h3 className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-4">Your official report includes</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2">
                {[
                  'Exact location (coordinates + address)',
                  'Date and time of observation',
                  'Temperature, humidity, wind, conditions',
                  'Nearest reporting station data',
                  'Timestamped report ID',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-slate-700 py-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0"></span>
                    {item}
                  </div>
                ))}
              </div>
            </div>

            {checkingPayment ? (
              <div className="w-full p-6 bg-blue-50 border border-blue-200 text-center">
                <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                <p className="text-sm text-blue-700">Processing payment and generating report...</p>
              </div>
            ) : (
              <div className="bg-white border border-slate-200 p-8">
                <div className="space-y-4 mb-6">
                  {(USE_CASES[useCase]?.checkmarks || USE_CASES.personal.checkmarks).map((line, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-slate-700">
                      <svg className="w-4 h-4 text-emerald-600 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      <span>{line}</span>
                    </div>
                  ))}
                </div>

                {result.weather_data?.data_quality === 'limited' ? (
                  <div data-testid="download-preview-section">
                    <p className="text-xs text-slate-500 text-center mb-4">Generate a verified PDF weather report for this location and time.</p>
                    <button
                      onClick={handleGeneratePDF}
                      disabled={generating}
                      data-testid="generate-pdf-btn"
                      className="w-full px-6 py-3 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-slate-300 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                    >
                      <FileArrowDown size={20} weight="bold" />
                      {generating ? 'Redirecting to payment...' : 'Unlock Official Report \u2013 $9.99'}
                    </button>
                    {downloadedReportId && (
                      <button
                        onClick={() => setShowShareModal(true)}
                        data-testid="share-report-btn"
                        className="w-full mt-2 px-6 py-2.5 border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                      >
                        <ShareNetwork size={18} weight="bold" />
                        Share Report
                      </button>
                    )}
                    <p className="text-xs text-slate-500 text-center mt-3">Download immediately after payment. No waiting.</p>
                  </div>
                ) : (
                  <>
                    <p className="text-xs text-slate-500 text-center mb-4">Generate a verified PDF weather report for this location and time.</p>
                    <button
                      onClick={handleGeneratePDF}
                      disabled={generating}
                      data-testid="generate-pdf-btn"
                      className="w-full px-6 py-3 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-slate-300 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                    >
                      <FileArrowDown size={20} weight="bold" />
                      {generating ? 'Redirecting to payment...' : 'Unlock Official Report \u2013 $9.99'}
                    </button>
                    <p className="text-xs text-slate-500 text-center mt-3">Download immediately after payment. No waiting.</p>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Disclaimer */}
        <div className="mt-8 p-4 border border-slate-200 bg-slate-50">
          <p className="text-xs text-slate-600 leading-relaxed">
            <strong className="text-slate-900">DISCLAIMER:</strong> This report is generated using historical weather data from third-party data providers. 
            While data is sourced from reputable services, accuracy is not guaranteed. This report is provided for informational 
            and documentation purposes only. Acceptance by courts, insurance companies, municipalities, or other entities is not 
            guaranteed. The user is responsible for verifying the suitability of this report for their specific use case.
          </p>
        </div>
      </main>
      {showShareModal && downloadedReportId && (
        <ShareReportModal
          reportId={downloadedReportId}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  );
};

export default WeatherLookup;
